import { Module } from 'vuex';
import SupplierInterface from '@/interfaces/SupplierInterface';
import SalutationInterface from '@/interfaces/SalutationInterface';
import {
  OrderAPI,
} from '@/api';
import BillingTypeInterface from '@/interfaces/BillingTypeInterface';

let storedSteps: string | Record<string, boolean> = `${localStorage.getItem('order-steps') || ''}`.trim();

if (storedSteps !== '') {
  storedSteps = JSON.parse(Buffer.from(storedSteps, 'base64').toString('utf-8'));
} else {
  storedSteps = {
    address: false,
    connection: false,
    payment: false,
  };
}

const orderModule: Module<any, any> = {
  namespaced: true,

  state: {
    status: 'fetching',
    fetchedFromBackend: false,
    form: {
      address: {
        customerType: null as string | null,
        salutation: null as string | null,
        firstName: null as string | null,
        lastName: null as string | null,
        namePersonOne: null as string | null,
        namePersonTwo: null as string | null,
        birthday: null as string | null,
        companyName: null as string | null,
        companyContactName: null as string | null,
        companyRegistrationCourt: null as string | null,
        companyRegistrationNumber: null as string | null,
        companyTaxId: null as string | null,
        houseNumberAddon: null as string | null,
        billingAddressDifferent: false,
        billingAddress: {
          postcode: null as string | number | null,
          city: null as string | null,
          street: null as string | null,
          houseNumber: null as string | null,
          houseNumberAddon: null as string | null,
        },
        phoneType: 'private',
        phone: null as string | null,
        email: null as string | null,
        emailConfirm: null as string | null,
      },
      connection: {
        isCustomer: false,
        customerNumber: null as string | null,
      },
      payment: {
        paymentType: '1',
        iban: null as string | null,
        accountOwner: null as string | null,
        sepa: false,
        billingType: null as string | null,
        yearly: false,
        coupon: null as string | null,
      },
      overview: {},
    },
    completedSteps: storedSteps,
    suppliers: {
      power: [] as SupplierInterface[],
      gas: [] as SupplierInterface[],
    },
    salutations: {
      private: [] as SalutationInterface[],
      business: [] as SalutationInterface[],
    },
    billingTypes: [] as BillingTypeInterface[],
    lastOrderedItem: null as null | Record<string, unknown>,
  },

  mutations: {
    SET_SUPPLIERS(state, suppliers: { power: SupplierInterface[], gas: SupplierInterface[] }) {
      state.suppliers = suppliers;
    },
    SET_SALUTATIONS(state, salutations: SalutationInterface[]) {
      state.salutations = salutations;
    },
    SET_BILLING_TYPES(state, billingTypes: BillingTypeInterface[]) {
      state.billingTypes = billingTypes;
    },
    SET_FORM_DATA(state, data) {
      state.form = {
        ...state.form,
        ...data,
      };
    },
    SET_STATUS(state, status: string) {
      state.status = status;
    },
    SET_LAST_ORDERED_ITEM(state, item: null | Record<string, unknown>) {
      state.lastOrderedItem = item;
    },
    SET_STEP_COMPLETED(state, { step, completed }) {
      state.completedSteps[step] = completed;
    },
    CLEAR_STEP_COMPLETED(state) {
      localStorage.removeItem('order-steps');
      Object.keys(state.completedSteps).forEach((key) => {
        state.completedSteps[key] = false;
      });
    },
    SET_FETCHED_FROM_BACKEND(state) {
      state.fetchedFromBackend = true;
    },
  },

  actions: {
    setSuppliers({ commit }, suppliers: { power: SupplierInterface[], gas: SupplierInterface[] }) {
      commit('SET_SUPPLIERS', suppliers);
    },
    setSalutations({ commit }, salutations: SalutationInterface[]) {
      commit('SET_SALUTATIONS', salutations);
    },
    setBillingTypes({ commit }, billingTypes: BillingTypeInterface[]) {
      commit('SET_BILLING_TYPES', billingTypes);
    },
    storeAndSave(state, { step, data }) {
      return new Promise((resolve, reject) => {
        OrderAPI.validate(step, data).then((response) => {
          resolve(response);
        }).catch((e) => {
          reject(e);
        });
      });
    },
    submit(state, { data }) {
      return new Promise((resolve, reject) => {
        OrderAPI.submit(data).then((response) => {
          resolve(response);
        }).catch((e) => {
          reject(e);
        });
      });
    },
    setFormData({ commit }, data) {
      commit('SET_FORM_DATA', data);
    },
    setStatus({ commit }, status) {
      commit('SET_STATUS', status);
    },
    setLastOrderedItem({ commit }, item) {
      commit('SET_LAST_ORDERED_ITEM', item);
    },
    deleteLastOrderedItem({ commit }) {
      OrderAPI.deleteLastOrderedItem();
      commit('SET_LAST_ORDERED_ITEM', null);
    },
    setStepCompleted({ commit, state }, data) {
      commit('SET_STEP_COMPLETED', data);
      localStorage.setItem('order-steps', Buffer.from(JSON.stringify(state.completedSteps), 'utf-8').toString('base64'));
    },
  },

  getters: {
    fetchedFromBackend: (state) => state.fetchedFromBackend,
    formData: (state) => state.form,
    suppliers: (state) => (branch: string): SupplierInterface[] => state.suppliers[branch === 'electricity' ? 'power' : branch],
    hasSupplier: (state) => (branch: string) => state.suppliers[branch].length > 0,
    salutations: (state) => (customerType: string): SalutationInterface[] => state.salutations[customerType],
    hasSalutations: (state) => (customerType: string): boolean => state.salutations[customerType].length > 0,
    billingTypes: (state) => (state.billingTypes || []).sort((billingTypeA: BillingTypeInterface) => {
      let sortingA = 2;
      const sortingB = 1;
      if (billingTypeA.value === 'billingTypes.online.active') {
        sortingA = 0;
      }

      return sortingA - sortingB;
    }),
    hasBillingTypes: (state) => state.billingTypes.length > 0,
    lastOrderedItem: (state) => state.lastOrderedItem,
    completedSteps: (state) => state.completedSteps,
    getSalutationById: (state) => (salutationId: string): string | null => {
      let salutation = null;

      Object.keys(state.salutations).forEach((customerType) => {
        const csal = state.salutations[customerType].find((sal: { value: string, label: string }) => sal.value === salutationId);

        if (csal) {
          salutation = csal.label;
        }
      });

      return salutation;
    },
  },
};

export default orderModule;
